// @flow
import React from 'react';
import {
  HomeOutlined,
  ReceiptOutlined,
  Home,
  Receipt,
  LocalMall,
  Settings,
  Storefront,
  AssignmentInd,
  AssignmentIndOutlined,
  AccountTree,
  AccountTreeOutlined,
  Timeline,
} from '@material-ui/icons';
import LocalMallOutlinedIcon from '@material-ui/icons/LocalMallOutlined';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import TimelineOutlinedIcon from '@material-ui/icons/TimelineOutlined';
import StorefrontOutlinedIcon from '@material-ui/icons/StorefrontOutlined';
import ChatOutlinedIcon from '@material-ui/icons/ChatOutlined';
import ChatIcon from '@material-ui/icons/Chat';

import type {NavigationConfig} from './types';

/**
 * Icon resolution map
 * Maps iconKey strings to React icon components
 */
export const ICONS = {
  // Default icons
  'home-outlined': <HomeOutlined />,
  'home-filled': <Home />,
  'orders-outlined': <ReceiptOutlined />,
  'orders-filled': <Receipt />,
  'products-outlined': <LocalMallOutlinedIcon />,
  'products-filled': <LocalMall />,
  'marketing-outlined': <TimelineOutlinedIcon />,
  'marketing-filled': <Timeline />,
  'settings-outlined': <SettingsOutlinedIcon />,
  'settings-filled': <Settings />,
  'store-outlined': <StorefrontOutlinedIcon />,
  'store-filled': <Storefront />,
  'customers-outlined': <AssignmentIndOutlined />,
  'customers-filled': <AssignmentInd />,
  'integrations-outlined': <AccountTreeOutlined />,
  'integrations-filled': <AccountTree />,
  'chat-outlined': <ChatOutlinedIcon />,
  'chat-filled': <ChatIcon />,
};

/**
 * Global navigation configuration
 * Single source of truth for all navigation (sidebar, bottom bar, search, etc.)
 */
export const PAGES: NavigationConfig = [
  {
    id: 'store-administration',
    header: 'ADMINISTRACIÓN DE TIENDA',
    items: [
      {
        id: 'home',
        label: 'Inicio',
        iconKey: 'home-outlined',
        path: '/home',
        keywords: ['dashboard', 'inicio', 'home'],
        tooltipItems: [
          {
            name: 'Dashboard',
            path: '/',
          },
        ],
      },
      {
        id: 'orders',
        label: 'Mis Pedidos',
        iconKey: 'orders-outlined',
        path: '/orders',
        onlyForBusinessModels: ['PRODUCT'],
        keywords: ['pedidos', 'orders', 'ventas'],
        children: [
          {
            id: 'orders-list',
            label: 'Pedidos',
            path: '/orders',
          },
          {
            id: 'manual-order',
            label: 'Crear Pedido Manual',
            path: '/orders/manual-order',
          },
        ],
      },
      {
        id: 'products',
        label: 'Productos',
        iconKey: 'products-outlined',
        path: '/products',
        onlyForBusinessModels: ['PRODUCT'],
        keywords: ['productos', 'products', 'catalog', 'catálogo'],
        children: [
          {
            id: 'products-list',
            label: 'Productos',
            path: '/products',
          },
          {
            id: 'organize-products',
            label: 'Ordenar Producto',
            path: '/organize-products',
          },
          {
            id: 'products-bulk-edit',
            label: 'Editor de productos',
            path: '/products-bulk-edit',
          },
          {
            id: 'products-bulk-upload',
            label: 'Creación/Edición masiva',
            path: '/products-bulk-upload',
            upSellIfMissingFeatures: ['MASSIVE_PRODUCT_UPLOAD'],
          },
          {
            id: 'categories',
            label: 'Categorías y Subcategorías',
            path: '/categories',
          },
          {
            id: 'products-variants',
            label: 'Variantes de producto',
            path: '/products-variants',
          },
          {
            id: 'product-option',
            label: 'Opciones de producto',
            path: '/product-option',
            requiredFeatures: ['PRODUCT_OPTIONS'],

            badge: {
              untilISO: '2025-12-31',
              label: 'Nuevo',
              size: 'small',
              color: 'primary',
            },
          },
          {
            id: 'product-catalog-pdf',
            label: 'Catálogo en PDF',
            path: '/v2/pages/product-catalog',
            target: '_blank',
          },
        ],
      },
      {
        id: 'services',
        label: 'Mis Servicios',
        iconKey: 'products-outlined',
        path: '/products',
        onlyForBusinessModels: ['SERVICE'],
        keywords: ['servicios', 'services'],
        children: [
          {
            id: 'services-list',
            label: 'Servicios',
            path: '/products',
          },
          {
            id: 'organize-services',
            label: 'Ordenar Servicio',
            path: '/organize-products',
          },
          {
            id: 'services-bulk-upload',
            label: 'Creación/Edición masiva',
            path: '/products-bulk-upload',
            upSellIfMissingFeatures: ['MASSIVE_PRODUCT_UPLOAD'],
          },
          {
            id: 'services-categories',
            label: 'Categorías y Subcategorías',
            path: '/categories',
          },
          {
            id: 'services-catalog-pdf',
            label: 'Catálogo en PDF',
            path: '/v2/pages/product-catalog',
            target: '_blank',
          },
        ],
      },
      {
        id: 'marketing',
        label: 'Marketing',
        iconKey: 'marketing-outlined',
        path: '/marketing',
        keywords: ['marketing', 'cupones', 'promociones', 'reseñas'],
        children: [
          {
            id: 'coupons',
            label: 'Cupones de descuento',
            path: '/marketing/coupons',
            onlyForBusinessModels: ['PRODUCT'],
            requiredFeatures: ['STORE_COUPON'],
          },
          {
            id: 'promotions',
            label: 'Promociones',
            path: '/marketing/promotions',
            onlyForBusinessModels: ['PRODUCT'],
            requiredFeatures: ['STORE_PROMOTION'],
            badge: {
              label: 'Nuevo',
              size: 'small',
              color: 'primary',
            },
          },
          {
            id: 'reviews',
            label: 'Reseñas',
            path: '/marketing/reviews',
            onlyForBusinessModels: ['PRODUCT'],
            requiredFeatures: ['ORDER_REVIEW'],
          },
          {
            id: 'templates',
            label: 'Plantillas',
            path: '/marketing/templates',
            requiredFeatures: ['MARKETING_TEMPLATES'],
          },
        ],
      },
      {
        id: 'configuration',
        label: 'Configuración',
        iconKey: 'settings-outlined',
        path: '/configure',
        keywords: ['configuración', 'settings', 'general', 'pago', 'envíos'],
        children: [
          {
            id: 'general-info',
            label: 'Información General',
            path: '/configure/general',
          },
          {
            id: 'customize-store',
            label: 'Logo y Diseño',
            path: '/configure/customize',
          },
          {
            id: 'payment-methods',
            label: 'Métodos de pago',
            path: '/configure/payment-methods',
          },
          {
            id: 'logistics',
            label: 'Envíos y Recogida',
            path: '/configure/logistics',
          },
          {
            id: 'sales-process',
            label: 'Ventas',
            path: '/configure/sales-process',
            onlyForBusinessModels: ['PRODUCT'],
          },
          {
            id: 'taxes',
            label: 'Impuestos',
            path: '/configure/taxes',
            onlyForCountries: ['PE'],
            upSellIfMissingFeatures: ['TAXES'],
            badge: {
              label: 'Nuevo',
              color: 'primary',
              size: 'small',
            },
          },
          {
            id: 'accounts',
            label: 'Usuarios',
            path: '/configure/accounts',
            onlyForRoles: ['vendor-user'],
            upSellIfMissingFeatures: ['STORE_COLLABORATOR'],
            badge: {
              label: 'Nuevo',
              color: 'primary',
              size: 'small',
              untilISO: '2025-08-18',
            },
          },
        ],
      },
      {
        id: 'customers',
        label: 'Clientes',
        iconKey: 'customers-outlined',
        path: '/customers',
        keywords: ['clientes', 'customers', 'crm', 'contactos', 'segmentos'],
        children: [
          {
            id: 'crm',
            label: 'CRM',
            path: '/customers',
            upSellIfMissingFeatures: ['CRM_CUSTOMER'],
          },
          {
            id: 'contacts',
            label: 'Contactos',
            path: '/contacts',
            requiredFeatures: ['CRM_SEGMENT'],
            upSellIfMissingFeatures: ['CRM_CUSTOMER'],
          },
          {
            id: 'segments',
            label: 'Segmentos',
            path: '/segments',
            requiredFeatures: ['CRM_SEGMENT'],
            upSellIfMissingFeatures: ['CRM_CUSTOMER'],
          },
          {
            id: 'campaigns',
            label: 'Campañas',
            path: '/campaigns',
            requiredFeatures: ['CRM_SEGMENT'],
            upSellIfMissingFeatures: ['CRM_CUSTOMER'],
          },
        ],
      },
    ],
  },
  {
    id: 'sales-channels',
    header: 'CANALES DE VENTA',
    items: [
      {
        id: 'virtual-store',
        label: 'Tienda Virtual',
        iconKey: 'store-outlined',
        path: '/my-store',
        keywords: ['tienda', 'store', 'páginas', 'dominio', 'blog'],
        children: [
          {
            id: 'pages',
            label: 'Páginas',
            path: '/my-store/pages',
            upSellIfMissingFeatures: ['STORE_POLICIES'],
          },
          {
            id: 'domain',
            label: 'Dominio',
            path: '/my-store/domain',
          },
          {
            id: 'social-medias',
            label: 'Redes Sociales',
            path: '/my-store/social-medias',
          },
          {
            id: 'blog',
            label: 'Blog',
            path: '/my-store/blog',
            upSellIfMissingFeatures: ['STORE_BLOG'],
          },
        ],
      },
      {
        id: 'integrations',
        label: 'Integraciones',
        iconKey: 'integrations-outlined',
        path: '/integrations',
        keywords: [
          'integraciones',
          'integrations',
          'google',
          'facebook',
          'tiktok',
        ],
        children: [
          {
            id: 'google-analytics',
            label: 'Google Analytics/GTM',
            path: '/integrations/google-analytics',
            upSellIfMissingFeatures: ['GOOGLE_ANALYTICS_TRACKING'],
          },
          {
            id: 'facebook-integration',
            label: 'Facebook / Instagram',
            path: '/integrations/facebook-integration',
          },
          {
            id: 'tiktok-pixel',
            label: 'TikTok Pixel',
            path: '/integrations/tiktok-pixel',
          },
          {
            id: 'tax-invoice',
            label: 'Facturación Electrónica',
            path: '/integrations/tax-invoice/dashboard',
            upSellIfMissingFeatures: ['TAX_INVOICE'],
            onlyForCountries: ['PE'],
          },
        ],
      },
      {
        id: 'chat',
        label: 'Chat',
        iconKey: 'chat-outlined',
        path: '/chat',
        upSellIfMissingFeatures: [
          'TIENDACHAT_WHATSAPP_WEB',
          'TIENDACHAT_INSTAGRAM',
          'TIENDACHAT_FB_MESSENGER',
        ],
        keywords: [
          'chat',
          'multichat',
          'whatsapp',
          'instagram',
          'facebook',
          'triggers',
        ],
        children: [
          {
            id: 'inbox',
            label: 'Inbox',
            path: '/chat/multi-chat',
            upSellIfMissingFeatures: [
              'TIENDACHAT_WHATSAPP_WEB',
              'TIENDACHAT_INSTAGRAM',
              'TIENDACHAT_FB_MESSENGER',
            ],
          },
          {
            id: 'channels',
            label: 'Canales',
            path: '/chat/channels',
            upSellIfMissingFeatures: [
              'TIENDACHAT_WHATSAPP_WEB',
              'TIENDACHAT_INSTAGRAM',
              'TIENDACHAT_FB_MESSENGER',
            ],
          },
          {
            id: 'triggers',
            label: 'Triggers',
            path: '/chat/triggers',
            upSellIfMissingFeatures: ['TIENDACHAT_TRIGGERS'],
          },
          {
            id: 'quick-replies',
            label: 'Respuestas rápidas',
            path: '/chat/quick-replies',
            upSellIfMissingFeatures: ['TIENDACHAT_QUICK_REPLIES'],
          },
          {
            id: 'funnels',
            label: 'Funnel',
            path: '/chat/funnels',
            upSellIfMissingFeatures: ['FUNNEL'],
          },
          {
            id: 'ai-chatbot',
            label: 'AI Chat Bot',
            path: '/store-bot/bots',
            requiredFeatures: ['TIENDACHAT_AI_CHATBOT'],
          },
        ],
      },
    ],
  },
];
